import { Box } from '@material-ui/core'
import { FC } from 'react'
import { Button, Modal, Typography } from 'src/modules/core/components'
import { useConfirmationOffsets } from 'src/modules/custom-offsets/hooks/use-confirmation-offsets'
import { useReminderOffsets } from 'src/modules/custom-offsets/hooks/use-reminder-offsets'
import { getExpiredConfirmationOffsets, getExpiredReminderOffsets } from 'src/modules/event/utils/helpers'
import { ReviewTimingIssuesLogic } from './review-timing-issues-logic'
import type { ReviewTimingIssuesModalProps } from './review-timing-issues-modal.interface'
import classes from './review-timing-issues-modal.module.scss'

export const ReviewTimingIssuesModal: FC<ReviewTimingIssuesModalProps> = ({
  confirmationEnabled,
  confirmationOffsets,
  reminderEnabled,
  reminderOffsets,
  eventDate,
  onSend,
  onClose,
  onBack,
}) => {
  const confirmationOffsetOptions = useConfirmationOffsets()
  const reminderOffsetOptions = useReminderOffsets()

  const result = ReviewTimingIssuesLogic.evaluate({
    confirmation_enabled: confirmationEnabled,
    confirmation_offsets: confirmationOffsets,
    reminder_enabled: reminderEnabled,
    reminder_offsets: reminderOffsets,
    event_date: eventDate,
  })

  const { offerImmediateConfirmation, offerImmediateReminder, warnConfirmationWillNotSend, warnOverlap } = result
  const hasImmediateOffer = offerImmediateConfirmation || offerImmediateReminder

  // Data for the Confirmation message
  const overdueConfimation = ReviewTimingIssuesLogic.getMinExpiredConfirmationOffset(eventDate, confirmationOffsets)
  const overdueConfimationName = confirmationOffsetOptions.find((option) => +option.value === overdueConfimation)?.label
  const expiredConfirmationOffsets = getExpiredConfirmationOffsets(
    eventDate,
    confirmationOffsetOptions.map((option) => +option.value),
  )
  const nextPossibleConfirmation = confirmationOffsetOptions
    .filter((option) => +option.value)
    .filter((option) => !expiredConfirmationOffsets.includes(+option.value))
    .find((option) => +option.value < overdueConfimation)?.short

  // Data for the Reminder message
  const overdueReminder = ReviewTimingIssuesLogic.getMinExpiredReminderOffset(eventDate, reminderOffsets)
  const overdueReminderName = reminderOffsetOptions.find((option) => +option.value === overdueReminder)?.label
  const expiredReminderOffsets = getExpiredReminderOffsets(
    eventDate,
    reminderOffsetOptions.map((option) => +option.value),
  )
  const nextPossibleReminder = reminderOffsetOptions
    .filter((option) => +option.value)
    .filter((option) => !expiredReminderOffsets.includes(+option.value))
    .find((option) => +option.value < overdueReminder)?.short

  return (
    <Modal
      variant="withBack"
      onClose={onClose}
      onBack={onBack}
      title={
        <Typography variant="h3" className={classes.modalTitle}>
          {hasImmediateOffer ? 'Send now?' : 'Please review'}
        </Typography>
      }
    >
      <Box marginBottom={3} display="flex" flexDirection="column" gridGap={12}>
        {offerImmediateConfirmation && (
          <>
            <Typography variant="body" className={classes.message}>
              The Confirmation message for {overdueConfimationName} is in the past.{' '}
              {nextPossibleConfirmation && (
                <>
                  Set a Confirmation for <strong>{nextPossibleConfirmation}</strong> or less.{' '}
                </>
              )}
            </Typography>
            <Typography variant="body" className={classes.message}>
              You can {nextPossibleConfirmation ? 'also' : ''} choose to{' '}
              <strong>send the Confirmation immediately</strong>.
            </Typography>
          </>
        )}

        {offerImmediateReminder && (
          <>
            <Typography variant="body" className={classes.message}>
              The Reminder message for {overdueReminderName} is in the past.{' '}
              {nextPossibleReminder && (
                <>
                  Set a Reminder for <strong>{nextPossibleReminder}</strong> or less.{' '}
                </>
              )}
            </Typography>
            <Typography variant="body" className={classes.message}>
              You can {nextPossibleReminder ? 'also' : ''} choose to <strong>send the Reminder immediately</strong>
              {warnConfirmationWillNotSend ? ', but the Confirmation message will not be sent' : ''}.
            </Typography>
          </>
        )}

        {warnConfirmationWillNotSend && !offerImmediateReminder && (
          <>
            <Typography variant="body" className={classes.message}>
              The Confirmation message for {overdueConfimationName} is in the past.
            </Typography>
            <Typography variant="body" className={classes.message}>
              We can send the Reminder, but <strong>the Confirmation message will not be sent</strong>.
            </Typography>
          </>
        )}

        {warnOverlap && (
          <Typography variant="body" className={classes.message}>
            Your Confirmation message will be sent <strong>after</strong> the Reminder message.
          </Typography>
        )}
      </Box>

      <Box display="flex" gridGap={12}>
        <Button variant="secondary" onClick={onBack}>
          Back to editing
        </Button>
        <Button onClick={onSend}>
          {hasImmediateOffer
            ? 'Send now'
            : warnConfirmationWillNotSend
              ? 'Schedule Reminder'
              : warnOverlap
                ? 'Schedule messages'
                : 'Send anyway'}
        </Button>
      </Box>
    </Modal>
  )
}
